import { gql } from '@apollo/client';

/**
 * The modal's own fresh read of the order: status, `split_blockers` (a jsonb array of codes), `last_modified_at`,
 * the item lines, the service lines (placed on the new orders in the board's services mode; `type` and
 * `require_confirm` show on each service card), and the open claims (placed in claims mode). Open means
 * `claim_status` is not `REJECTED` or `CLOSED`, with no status counting as open, the same test `split_order` uses;
 * the server requires every open claim to be listed under one child. The item selection carries what
 * `itemStatusTag` needs: pallet warehouse status, the exception `type` and `status`, `intangible`, and `quantity`.
 * Aliased to `items` for callers. The remaining item, service, and claim fields fill the card details dialog
 * (`itemDetailRows`, `serviceDetailRows`, `claimDetailRows`).
 * Run it with `fetchPolicy: 'network-only'` and skip it while the modal is closed (see `useSplitOrderRead`).
 */
export const SPLIT_ORDER_READ = gql`
    query SplitOrderRead($order_id: uuid!) {
        orders(where: { order_id: { _eq: $order_id } }, limit: 1) {
            order_id
            order_number
            order_status
            last_modified_at
            split_blockers
            items: itemsByOrderId(order_by: { created_at: asc }) {
                item_id
                sku
                description
                quantity
                intangible
                barcode
                line_id
                serial_number
                item_type_details
                length
                width
                height
                total_weight
                total_cubes
                is_return
                disposition
                exceptions {
                    exception_type
                    exception {
                        exception_id
                        type
                        status
                    }
                }
                pallet {
                    pallet_id
                    warehouse_status
                }
            }
            services(order_by: [{ created_at: asc }, { service_id: asc }]) {
                service_id
                code
                description
                quantity
                type
                require_confirm
                size
                billable
                confirmed
                confirmed_quantity
                service_time
            }
            claims(
                where: {
                    _or: [{ claim_status: { _is_null: true } }, { claim_status: { _nin: ["REJECTED", "CLOSED"] } }]
                }
                order_by: [{ created_at: asc }, { claim_id: asc }]
            ) {
                claim_id
                claim_number
                claim_type
                claim_status
                freight_amount
                incident_date
            }
        }
    }
`;

/**
 * The Split Order button's read: `split_blockers` and the item lines' `intangible` flag, for the line-count rule.
 * Kept apart from the shared order query because `split_blockers` is a computed field for role `user` only.
 */
export const SPLIT_BUTTON_READ = gql`
    query SplitButtonRead($order_id: uuid!) {
        orders(where: { order_id: { _eq: $order_id } }, limit: 1) {
            order_id
            order_status
            split_blockers
            items: itemsByOrderId {
                item_id
                intangible
            }
        }
    }
`;
